<template>
	<div class="page" v-if="page">
		<section class="section">
			<div class="container is-fullhd">
				<div class="columns">
					<div class="column">
						<RankedStartingPlayers
							heading="Best Starting Players"
							:roster_positions="page.roster_positions"
							players_key="best_players"
						/>
					</div>

					<div class="column">
						<RankedStartingPlayers
							heading="Worst Starting Players"
							:roster_positions="page.roster_positions"
							players_key="worst_players"
						/>
					</div>
				</div>
			</div>
		</section>

		<section class="section is-hidden-mobile">
			<div class="container is-fullhd">
				<div class="content">
					<h2 class="has-text-centered">Ranked Teams</h2>
					<p class="has-text-centered">by total best and worst players</p>

					<Chart :config="charts.ranked_teams" />
				</div>
			</div>
		</section>

		<section class="section">
			<div class="container is-fullhd">
				<div class="box content">
					<div class="is-flex is-flex-apart">
						<h2>Ranked Players by Points</h2>
					</div>

					<div class="is-flex is-flex-spaced is-flex-wrap is-flex-vcenter">
						<Dropdown
							alignment="left"
							:options="availableRosterPositions"
							:optionText="(option) => option.position"
							:handleSelectedOption="setFilterPosition"
						/>
						<Dropdown
							class="is-hidden-tablet"
							:options="pointsOptions"
							:optionText="(option) => option.text"
							:handleSelectedOption="setPointsOption"
						/>
						<Dropdown :options="page.teams" :optionText="(option) => option.name" :handleSelectedOption="setHighlightTeam" />
						<label class="checkbox is-hidden-mobile">
							<input type="checkbox" v-model="showAllWeeks" />
							Show all weeks
						</label>
						<label class="checkbox is-hidden-tablet" v-if="pointsOption.key == 'weeks'">
							<input type="checkbox" v-model="showAllWeeks" />
							Show all weeks
						</label>
					</div>

					<div class="table-scroll-container is-hidden-mobile">
						<div class="table-scroll">
							<table class="table is-fullwidth is-narrow">
								<thead>
									<tr>
										<th class="has-text-right">#</th>
										<th>Player</th>
										<th>Team/Pos.</th>
										<th>Owner</th>
										<th class="has-text-right">Points</th>
										<th class="has-text-right">Average</th>
										<th class="has-text-right">Std.&nbsp;Dev.</th>
										<template v-for="week in filteredRankedWeeks">
											<th class="has-text-centered">Wk&nbsp;{{ week }}</th>
										</template>
									</tr>
								</thead>
								<tbody>
									<template v-for="(data, index) in filteredPlayers">
										<tr :class="{ 'is-highlight': data.team && highlightTeam && data.team.key == highlightTeam.key }">
											<td class="has-text-right">{{ index + 1 }}</td>
											<td>{{ data.player.name }}</td>
											<td>{{ data.player.team }} - {{ data.player.position }}</td>
											<td><TeamLink :teamKey="data.team.key" :teamName="data.team.name" v-if="data.team" /></td>
											<td class="has-text-right">{{ data.points_season }}</td>
											<td class="has-text-right">{{ data.points_average }}</td>
											<td class="has-text-right">{{ data.points_stddev }}</td>
											<template v-for="week_rank in getPlayerRankedWeeks(data)">
												<td v-if="week_rank" class="week-rank-box" :class="getTierStyleClass(week_rank.tier)">
													<div>
														<span class="points">{{ week_rank.points }}</span>
														<span class="rank">{{ week_rank.rank }}</span>
													</div>
												</td>
												<td v-else></td>
											</template>
										</tr>
									</template>
								</tbody>
							</table>
						</div>
					</div>

					<div class="table-scroll-container is-hidden-tablet">
						<div class="table-scroll">
							<table class="table is-fullwidth is-narrow">
								<thead>
									<tr>
										<th class="has-text-right">#</th>
										<th>Player</th>
										<th v-if="filterPositionIndex == 0">Team/Pos</th>
										<th v-else>Team</th>
										<th v-if="pointsOption.key != 'weeks'" class="has-text-right">Points</th>
										<template v-else v-for="week in filteredRankedWeeks">
											<th class="has-text-centered">Wk&nbsp;{{ week }}</th>
										</template>
									</tr>
								</thead>
								<tbody>
									<template v-for="(data, index) in filteredPlayers">
										<tr :class="{ 'is-highlight': data.team && highlightTeam && data.team.key == highlightTeam.key }">
											<td class="has-text-right">{{ index + 1 }}</td>
											<td>{{ data.player.name }}</td>
											<td v-if="filterPositionIndex == 0">{{ data.player.team }} - {{ data.player.position }}</td>
											<td v-else>{{ data.player.team }}</td>
											<td v-if="pointsOption.key != 'weeks'" class="has-text-right">{{ getPointsByOption(data) }}</td>
											<template v-else v-for="week_rank in getPlayerRankedWeeks(data)">
												<td v-if="week_rank" class="week-rank-box" :class="getTierStyleClass(week_rank.tier)">
													<div>
														<span class="points">{{ week_rank.points }}</span>
														<span class="rank">{{ week_rank.rank }}</span>
													</div>
												</td>
												<td v-else></td>
											</template>
										</tr>
									</template>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
import Chart from "@/components/Chart"
import Dropdown from "@/components/Dropdown"
import RankedStartingPlayers from "@/components/RankedStartingPlayers"
import TeamLink from "@/components/TeamLink"

const LIMIT_WEEKS_SIZE = 5

export default {
	components: {
		Chart,
		Dropdown,
		RankedStartingPlayers,
		TeamLink,
	},
	data() {
		return {
			filterPositionIndex: 0,
			highlightTeamIndex: 0,
			pointsOptionIndex: 0,
			pointsOptions: [
				{
					text: "Season",
					key: "points_season",
				},
				{
					text: "Average",
					key: "points_average",
				},
				{
					text: "Std. Dev.",
					key: "points_stddev",
				},
				{
					text: "Weeks",
					key: "weeks",
				},
			],
			showAllWeeks: false,
		}
	},
	computed: {
		page() {
			return this.$store.getters.activePage
		},
		charts() {
			return !this.page
				? {}
				: {
						ranked_teams: {
							type: "bar",
							series: this.page.graph_datas.ranked_teams.series,
							height: 500,
							options: {
								plotOptions: {
									bar: {
										horizontal: false,
										endingShape: "rounded",
										columnWidth: "55%",
									},
								},
								dataLabels: {
									enabled: false,
								},
								stroke: {
									show: true,
									width: 2,
									colors: ["transparent"],
								},
								xaxis: {
									categories: this.page.graph_datas.ranked_teams.labels,
								},
								fill: {
									opacity: 1,
								},
							},
						},
				  }
		},
		availableRosterPositions() {
			return this.page.roster_positions.filter(
				(roster_position, index) => index == 0 || this.page.players.some((data) => data.player.position == roster_position.position)
			)
		},
		filterPosition() {
			return this.availableRosterPositions[this.filterPositionIndex].position
		},
		highlightTeam() {
			return this.page.teams[this.highlightTeamIndex]
		},
		pointsOption() {
			return this.pointsOptions[this.pointsOptionIndex]
		},
		filteredRankedWeeks() {
			return this.page.ranked_weeks.slice(this.showAllWeeks ? 0 : -LIMIT_WEEKS_SIZE)
		},
		filteredPlayers() {
			return this.page.players.filter((data) => {
				if (this.filterPositionIndex > 0 && data.player.position != this.filterPosition) {
					return false
				}

				return true
			})
		},
	},
	methods: {
		setFilterPosition(index) {
			this.filterPositionIndex = index
		},
		setHighlightTeam(index) {
			this.highlightTeamIndex = index
		},
		setPointsOption(index) {
			this.pointsOptionIndex = index
		},
		getPointsByOption(data) {
			return data[this.pointsOption.key]
		},
		getPlayerRankedWeeks(data) {
			return data.weeks_ranks.slice(this.showAllWeeks ? 0 : -LIMIT_WEEKS_SIZE)
		},
		getTierStyleClass(tier) {
			return `week-rank-tier-${Math.min(tier + 1, 5)}`
		},
	},
	mounted() {
		this.$store.dispatch("setActivePage", "players_dashboard")
	},
}
</script>

<style lang="scss" scoped>
$green: hsl(141, 53%, 53%);
$yellow: hsl(48, 100%, 67%);
$red: hsl(348, 86%, 61%);

.week-rank-tier-1 {
	$color: mix($green, white, 75%);
	background-color: $color !important;
}
.week-rank-tier-2 {
	$color: mix(mix($green, $yellow, 50%), white, 75%);
	background-color: $color !important;
}
.week-rank-tier-3 {
	$color: mix($yellow, white, 75%);
	background-color: $color !important;
}
.week-rank-tier-4 {
	$color: mix(mix($red, $yellow, 50%), white, 75%);
	background-color: $color !important;
}
.week-rank-tier-5 {
	$color: mix($red, white, 75%);
	background-color: $color !important;
}

.week-rank-box {
	padding: 0;
	vertical-align: middle;
}

.week-rank-box > div {
	align-items: center;
	display: flex;
	flex-flow: column;
	height: 100%;
	justify-content: center;
	line-height: 1;
}

.week-rank-box .points {
	font-size: 0.7rem;
}

.week-rank-box .rank {
	font-size: 0.7rem;
}
</style>
